import React, { FC, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { get, set, without } from 'lodash';

import integrationsResource, {
  AutoLinkFields,
  ExternalSource,
  ExternalSourceTypes,
} from 'src/actions/integrations/integrationsResource';

import { DomainError } from 'src/models/DomainError';
import { Body } from 'src/presentation/components/forms/Layout';
import { Button } from 'src/presentation/components/agent_desktop_components';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { FormEditorProps, useForm } from 'src/presentation/components/forms/Form';
import { H1 } from '@ad-scripts/presentation/components/common/headers';

import {
  ActionsSupport,
  AutoLinkSupportForEmailAndPhone,
  CustomerLookupSupport,
  CustomerSearchSupport,
  IntegrationDisplayLabels,
  MaxQueryAttributeCount,
  RequiredSearchQueryFieldNames,
  RequiredSearchResultFieldNames,
} from 'src/presentation/components/pages/integrations/constants';

import ActionSettingsEditor from 'src/presentation/components/pages/integrations/editorComponents/actionSettings/ActionSettingsEditor';
import ConfigurationEditor from 'src/presentation/components/pages/integrations/editorComponents/ConfigurationEditor';
import {
  buildCustomHeaderList,
  buildCustomHeaderMap,
  buildDefaultItem,
  nameValueListToSearchAttrs,
  searchAttrsToNameValueList,
  updateUnsetActionSettings,
  verifyNameValueList,
} from 'src/presentation/components/pages/integrations/editorComponents/helpers';
import LookupSettingsEditor, {
  AutoLinkFieldProps,
} from 'src/presentation/components/pages/integrations/editorComponents/LookupSettingsEditor';
import { NameValueList } from 'src/presentation/components/pages/integrations/types';
import useRouter from 'src/presentation/hooks/useRouter';

/**
 * List of all Integration Types that we currently support (i.e. have some editor UI for that type)
 */
const AllowedIntegrationTypes = [
  ExternalSourceTypes.Shopify,
  ExternalSourceTypes.Magento,
  ExternalSourceTypes.LoopReturns,
  ExternalSourceTypes.BigCommerce,
  ExternalSourceTypes.Klaviyo,
  ExternalSourceTypes.Segment,
  ExternalSourceTypes.Zaius,
  ExternalSourceTypes.AwsEventBridge,
  ExternalSourceTypes.Smile,
  ExternalSourceTypes.Ordergroove,
  ExternalSourceTypes.Recharge,
  ExternalSourceTypes.Qualtrics,
  ExternalSourceTypes.Yotpo,
  ExternalSourceTypes.Custom,
];

const IntegrationEditor: FC<FormEditorProps> = React.memo(({ errors, item, root }): JSX.Element | null => {
  const dispatch = useDispatch();
  const { history, location } = useRouter();
  const query = new URLSearchParams(location?.search);
  const itemType = item?.type || query.get('type');
  const initializedRef = useRef(false);

  const integrationType = AllowedIntegrationTypes.find(
    sourceType => sourceType.toLowerCase() === (itemType || '').toLowerCase()
  );

  const requiredSearchQueryFields = integrationType ? RequiredSearchQueryFieldNames[integrationType] : [];
  const requiredSearchResultFields = integrationType ? RequiredSearchResultFieldNames[integrationType] : [];

  // Temporary refs for editing various lists (custom headers, query attributes etc.)
  const originalIntegrationItem = item || buildDefaultItem(integrationType);
  const updatedActionSettings = updateUnsetActionSettings(integrationType, originalIntegrationItem['actionSettings']);
  const updatedItem = { ...originalIntegrationItem };

  if (updatedActionSettings) {
    updatedItem['actionSettings'] = updatedActionSettings['actionSettings'];
  }
  const workingItem = updatedItem;

  const customHeadersRef = useRef<NameValueList>(buildCustomHeaderList(workingItem));
  const searchQueryAttrsRef = useRef<NameValueList>(
    searchAttrsToNameValueList(workingItem, 'customerLookupSettings.searchQueryAttributes', requiredSearchQueryFields)
  );
  const searchResultAttrsRef = useRef<NameValueList>(
    searchAttrsToNameValueList(workingItem, 'customerLookupSettings.searchResultAttributes', requiredSearchResultFields)
  );

  useEffect(() => {
    if (!integrationType) {
      // We don't know how to edit items of this type. Go back to where we came from
      history.replace(root);
      return;
    }

    if (!initializedRef.current) {
      dispatch(integrationsResource.actions.resetErrors({}));
      initializedRef.current = true;
    }
  });

  const formContext = useForm<ExternalSource>({
    add: integrationsResource.effects.addWithTransform,
    update: integrationsResource.effects.updateWithTransform,
    validate: integrationsResource.effects.validateItem,
    resetErrors: integrationsResource.actions.resetErrors,
    defaultItem: buildDefaultItem(integrationType),
    errors,
    item: workingItem,
    root,
    saveAsNew: !item, // Controls whether we are trying to create a new item or update an existing one

    onSave: ({ defaultOnSave, updateState, workingStateRef }) => {
      const combinedErrors: DomainError[] = [];
      if (integrationType === ExternalSourceTypes.Custom) {
        const knownHeaders = workingStateRef.current.secrets?.hasCustomHeaders || [];
        const errors = verifyNameValueList(customHeadersRef.current, knownHeaders);
        combinedErrors.push(...errors);

        if (!errors.length) {
          // Convert temporary header list into the final structure and try to save
          const headers = buildCustomHeaderMap(customHeadersRef.current, knownHeaders);
          workingStateRef.current.secrets = { ...workingStateRef.current.secrets, ...{ headers } };
        }
      }

      // Remove trailing slash from Shopify URLs: CH #90307
      if (integrationType === ExternalSourceTypes.Shopify && workingStateRef.current?.connectionSettings?.baseUrl) {
        const url = workingStateRef.current.connectionSettings.baseUrl.trim();
        workingStateRef.current.connectionSettings.baseUrl =
          url.substr(-1) === '/' ? url.substr(0, url.length - 1) : url;
      }

      // Transfer search attributes from the temporary ref
      if (integrationType && CustomerLookupSupport[integrationType]) {
        const queryErrors = verifyNameValueList(searchQueryAttrsRef.current, []);
        const resultErrors = verifyNameValueList(searchResultAttrsRef.current, [], ['emails', 'phones']);
        combinedErrors.push(...queryErrors, ...resultErrors);

        if (!queryErrors.length && !resultErrors.length) {
          workingStateRef.current.customerLookupSettings = {
            ...workingStateRef.current.customerLookupSettings,
            ...{
              searchQueryAttributes: nameValueListToSearchAttrs(searchQueryAttrsRef.current),
              searchResultAttributes: nameValueListToSearchAttrs(searchResultAttrsRef.current),
            },
          };
        }
      }

      // For Ordergroove: if apiKey is provided, remove legacy fields before saving
      if (integrationType === ExternalSourceTypes.Ordergroove) {
        const secrets = workingStateRef.current.secrets || {};
        if (secrets.apiKey) {
          secrets.hashKey = '';
          secrets.apiUserToken = '';
          workingStateRef.current.secrets = secrets;
        }
      }

      if (!dispatch(integrationsResource.effects.validateItem(workingStateRef.current, combinedErrors))) return;
      updateState(workingStateRef.current);
      defaultOnSave();
    },
  });

  // Make sure we know how to handle this type of Integration
  if (!integrationType) return null;

  const integrationDisplayLabel = IntegrationDisplayLabels[integrationType];
  const currentWorkingState = formContext.workingStateRef.current;
  const isAutolinkFieldEnabled = (fieldName: string): boolean => {
    const configOption = get(currentWorkingState, 'customerLookupSettings.autoLinkFields') || [];
    const fields = configOption.map(field => field.toLowerCase());
    return fields.includes(fieldName.toLowerCase());
  };

  const toggleAutolinkField = (fieldName: string): void => {
    const enabled = isAutolinkFieldEnabled(fieldName);
    const fields = get(currentWorkingState, 'customerLookupSettings.autoLinkFields') || [];
    if (enabled) {
      set(currentWorkingState, 'customerLookupSettings.autoLinkFields', without(fields, fieldName));
    } else {
      set(currentWorkingState, 'customerLookupSettings.autoLinkFields', [...fields, fieldName]);
    }
  };

  const editorFields = (
    <ConfigurationEditor
      integrationType={integrationType}
      formWorkingState={formContext.workingStateRef}
      headerListWorkingState={customHeadersRef}
      resetError={formContext.resetError}
      errors={errors}
      maskedFields={(item as ExternalSource)?.secrets?.hasSecrets || []}
      maskedHeaders={(item as ExternalSource)?.secrets?.hasCustomHeaders || []}
    />
  );

  // Construct "abbreviated" and "full" lists of the auto-linking fields. Different integrations may use different
  // auto-linking field sets
  const autolinkFieldsWithEmail: AutoLinkFieldProps[] = [
    {
      field: AutoLinkFields.Email,
      displayLabel: 'Customer Email',
      value: isAutolinkFieldEnabled(AutoLinkFields.Email),
    },
  ];
  const autolinkFieldsWithEmailAndPhone: AutoLinkFieldProps[] = [
    {
      field: AutoLinkFields.Email,
      displayLabel: 'Customer Email',
      value: isAutolinkFieldEnabled(AutoLinkFields.Email),
    },
    { field: AutoLinkFields.Phone, displayLabel: 'Phone Number', value: isAutolinkFieldEnabled(AutoLinkFields.Phone) },
  ];

  const useAutoLinkFieldsWithEmailAndPhone = AutoLinkSupportForEmailAndPhone.includes(integrationType);
  const lookupEditor = CustomerLookupSupport[integrationType] ? (
    <LookupSettingsEditor
      autolinkFields={useAutoLinkFieldsWithEmailAndPhone ? autolinkFieldsWithEmailAndPhone : autolinkFieldsWithEmail}
      maxQueryAttributeCount={MaxQueryAttributeCount}
      onAutolinkFieldChange={toggleAutolinkField}
      overwriteFlagAttribute="customerLookupSettings.overwritingEnabled"
      queryAttrListRef={searchQueryAttrsRef}
      resultAttrListRef={searchResultAttrsRef}
      searchEnabled={CustomerSearchSupport[integrationType]}
    />
  ) : null;

  const actionsEditor = ActionsSupport[integrationType] ? (
    <ActionSettingsEditor dataAid="actions" integrationType={integrationType} />
  ) : null;

  return (
    <Form context={formContext}>
      <H1>{formContext.isNew ? `Add New ${integrationDisplayLabel}` : `Edit ${integrationDisplayLabel}`} App</H1>
      <Body data-aid="agent-status-editor">
        {editorFields}
        {lookupEditor}
        {actionsEditor}
      </Body>

      <Footer>
        <Button data-aid="integration-edit-cancel" type={Button.Types.TEXT} onClick={formContext.onClose}>
          Cancel
        </Button>
        <Button data-aid="integration-edit-save" onClick={formContext.onSave}>
          Save App
        </Button>
      </Footer>
    </Form>
  );
});

export default IntegrationEditor;
